import PropTypes from "prop-types"
import ReactSelect, { components as reactSelectComponents } from "react-select"
import { buildReactSelectStyles } from "../_reactSelectStyles"

/**
 * Renders a collapsed summary chip when more than 2 items are selected
 * and a collapsedLabel is provided. Only the first chip (index 0) renders
 * the summary label; all other chips return null so only one pill appears.
 */
const CollapsedMultiValue = ({ children, ...props }) => {
  const { index } = props
  const selectedCount = props.selectProps.value?.length || 0
  const totalCount = props.selectProps.options?.length || 0
  const collapsedLabel = props.selectProps.collapsedLabel

  // When 2 or fewer items are selected, render individual chips as normal
  if (selectedCount <= 2) {
    return (
      <reactSelectComponents.MultiValue {...props}>
        {children}
      </reactSelectComponents.MultiValue>
    )
  }

  // For items after the first, render nothing — only one summary pill needed
  if (index > 0) return null

  const label =
    selectedCount === totalCount
      ? `All ${collapsedLabel}`
      : `${selectedCount} ${collapsedLabel}`

  return (
    <reactSelectComponents.MultiValue {...props}>
      {label}
    </reactSelectComponents.MultiValue>
  )
}

CollapsedMultiValue.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number,
  selectProps: PropTypes.shape({
    value: PropTypes.array,
    options: PropTypes.array,
    collapsedLabel: PropTypes.string,
  }),
}

/**
 * AppMultiSelect — Multi-value select using react-select.
 *
 * Selected items render as pill chips with a remove button.
 * Styled via the shared buildReactSelectStyles factory (isMulti=true).
 *
 * Props (unchanged external API):
 * @param {Array}    options            — array of { value, label }
 * @param {Array}    value              — array of selected { value, label }
 * @param {Function} onChange           — (options: { value, label }[]) => void
 * @param {string}   placeholder        — placeholder text (default "Select...")
 * @param {boolean}  isDisabled         — disables the control
 * @param {boolean}  isLoading          — shows loading spinner
 * @param {string}   noOptionsMessage   — message when no options match (default "No options")
 * @param {boolean}  closeMenuOnSelect  — close dropdown after each selection (default false)
 * @param {Element|null} menuPortalTarget — portal target for the menu (default document.body)
 * @param {string}   collapsedLabel     — when provided and >2 items are selected, collapses
 *                                        chips to a single "N {label}" or "All {label}" pill
 */
export default function AppMultiSelect({
  options,
  value,
  onChange,
  placeholder,
  isDisabled,
  isLoading = false,
  noOptionsMessage = "No options",
  closeMenuOnSelect = false,
  menuPortalTarget = typeof document === "undefined" ? null : document.body,
  menuPosition = "fixed",
  menuPlacement = "bottom",
  collapsedLabel,
  "data-testid": dataTestId,
}) {
  // Only override MultiValue when collapsedLabel is provided
  const customComponents = collapsedLabel
    ? { MultiValue: CollapsedMultiValue }
    : undefined

  // Wrap in a div for data-testid — react-select doesn't forward DOM attributes
  const select = (
    <ReactSelect
      isMulti
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isLoading={isLoading}
      noOptionsMessage={() => noOptionsMessage}
      closeMenuOnSelect={closeMenuOnSelect}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
      menuPlacement={menuPlacement}
      styles={buildReactSelectStyles({ isMulti: true })}
      classNamePrefix="app-multiselect"
      collapsedLabel={collapsedLabel}
      components={customComponents}
    />
  )

  return dataTestId ? (
    <div data-testid={dataTestId}>{select}</div>
  ) : (
    select
  )
}

AppMultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  noOptionsMessage: PropTypes.string,
  closeMenuOnSelect: PropTypes.bool,
  menuPortalTarget: PropTypes.instanceOf(
    typeof Element === "undefined" ? Object : Element,
  ),
  menuPosition: PropTypes.oneOf(["absolute", "fixed"]),
  menuPlacement: PropTypes.oneOf(["auto", "bottom", "top"]),
  collapsedLabel: PropTypes.string,
  "data-testid": PropTypes.string,
}
